import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../db/firebase";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  deleteDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import GroupModal from "./GroupsModal";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { LoadingSpinner } from "../../../components/Loading";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineUserGroup,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineChevronRight,
  HiOutlineUsers,
} from "react-icons/hi2";

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors";

const glassCard =
  "rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm";

type Group = {
  id: string;
  name?: string;
  description?: string;
  organizationId?: string;
  members?: string[];
  leaders?: string[];
  [key: string]: any;
};

const Groups: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<Group | null>(null);
  const { organizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchGroups = async () => {
      if (!organizationId) return;
      setLoading(true);
      try {
        const groupsSnapshot = await getDocs(
          query(
            collection(db, "groups"),
            where("organizationId", "==", organizationId),
          ),
        );
        const groupsData = groupsSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as Group))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setGroups(groupsData);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [organizationId]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      const name = (g.name || "").toLowerCase();
      const description = (g.description || "").toLowerCase();
      return name.includes(q) || description.includes(q);
    });
  }, [groups, search]);

  const handleCreate = () => {
    setCurrentGroup(null);
    setShowModal(true);
  };

  const handleEdit = (group: Group) => {
    setCurrentGroup(group);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!groupToDelete) return;
    await deleteDoc(doc(db, "groups", groupToDelete.id));
    setGroups((prev) => prev.filter((g) => g.id !== groupToDelete.id));
    setShowConfirmDelete(false);
    setGroupToDelete(null);
  };

  const confirmDeleteGroup = (group: Group) => {
    setGroupToDelete(group);
    setShowConfirmDelete(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setCurrentGroup(null);
  };

  const handleSave = async (group: any) => {
    setSaving(true);
    try {
      if (currentGroup) {
        const groupDocRef = doc(db, "groups", currentGroup.id);
        await updateDoc(groupDocRef, group);
        setGroups((prev) =>
          prev.map((g) => (g.id === currentGroup.id ? { ...g, ...group } : g)),
        );
      } else {
        const newGroupDocRef = await addDoc(collection(db, "groups"), {
          ...group,
          organizationId,
          members: [],
          leaders: [],
        });
        setGroups((prev) =>
          [
            ...prev,
            {
              id: newGroupDocRef.id,
              ...group,
              organizationId,
              members: [],
              leaders: [],
            },
          ].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        );
      }
      handleModalClose();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" message={t("loading")} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
            {t("groups")}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t("groupsPageSubtitle")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {groups.length} {t("groups")?.toLowerCase() || "groups"}
          </p>
        </div>
        <button type="button" onClick={handleCreate} className={glassBtn}>
          <HiOutlinePlus className="w-5 h-5" />
          {t("addGroup")}
        </button>
      </div>

      <div className="relative">
        <HiOutlineMagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("searchGroups")}
          className="w-full pl-12 pr-10 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/70 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label={t("clear")}
          >
            <HiOutlineXMark className="w-4 h-4" />
          </button>
        )}
      </div>

      {filteredGroups.length === 0 ? (
        <div className={`${glassCard} flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400`}>
          <div className="w-14 h-14 mb-3 rounded-2xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center">
            <HiOutlineUserGroup className="w-7 h-7" />
          </div>
          <p className="text-sm font-medium">
            {search.trim() ? t("noSearchResults") : t("noGroupsAvailable")}
          </p>
          {!search.trim() && (
            <button type="button" onClick={handleCreate} className={`${glassBtn} mt-4`}>
              <HiOutlinePlus className="w-4 h-4" />
              {t("addGroup")}
            </button>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {filteredGroups.map((group) => {
            const memberCount = group.members?.length || 0;
            const leaderCount = group.leaders?.length || 0;
            return (
              <li key={group.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/groups/${group.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/groups/${group.id}`);
                    }
                  }}
                  className={`${glassCard} p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer hover:border-[#5932EA]/25 dark:hover:border-[#5932EA]/30 transition-all`}
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-11 h-11 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
                      <HiOutlineUserGroup className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 truncate">
                        {group.name}
                      </h2>
                      {group.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                          {group.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/50 dark:bg-gray-900/30 text-gray-600 dark:text-gray-300">
                          <HiOutlineUsers className="w-3.5 h-3.5" />
                          {memberCount} {t("members")?.toLowerCase() || "members"}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium border border-[#5932EA]/20 bg-[#5932EA]/8 text-[#4c2bd4] dark:text-[#c4b5fd]">
                          {leaderCount} {t("leaders")?.toLowerCase() || "leaders"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 self-end sm:self-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(group);
                      }}
                      className="p-2.5 rounded-xl text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors"
                      title={t("edit")}
                    >
                      <HiOutlinePencil className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteGroup(group);
                      }}
                      className="p-2.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors"
                      title={t("delete")}
                    >
                      <HiOutlineTrash className="w-5 h-5" />
                    </button>
                    <HiOutlineChevronRight className="w-5 h-5 text-gray-300 dark:text-gray-600 ml-1 hidden sm:block" />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showModal && (
        <GroupModal
          group={currentGroup}
          onSave={handleSave}
          onClose={handleModalClose}
          saving={saving}
        />
      )}
      {showConfirmDelete && (
        <ConfirmDeleteModal
          isOpen={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={handleDelete}
          userName={groupToDelete ? groupToDelete.name || "" : ""}
        />
      )}
    </div>
  );
};

export default Groups;
